import { useEffect, useState } from 'react'
import { api, type History } from '../api'
import { says, subjectText } from '../limits'

/**
 * A rule's change history — proposal §D, TECHNICAL_DESIGN.md §5.2, §5.4.
 *
 * Launch gate 6 is *reconstruct any rule's state at an arbitrary past date*, and
 * this is where a reader does it. Versions and decisions are shown together
 * because neither is the history on its own: the versions say what the limit
 * *was*, the events say who decided and when.
 *
 * **A superseded version is not greyed out of relevance.** It is the correct
 * answer for any date inside its own range (§5.2), so it is shown with the dates
 * it applied rather than as a faded prior draft — the spreadsheet's failure was
 * that the previous value simply vanished.
 *
 * **Both halves are tables.** They were lists of unlabelled inline spans, which
 * rendered as `activemax 51/8/2026 – in forcepack` — five separate facts run
 * together with nothing saying which was which. Every one of them needed a
 * heading, and a heading per fact repeated down a list is a table.
 */
function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function moment(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** The audit log's own verbs, as sentences. `rule.propose` is the store's name
 *  for the act and not a thing anybody says out loud. */
const ACTIONS: Record<string, string> = {
  'rule.propose': 'Proposed',
  'rule.activate': 'Approved and put into force',
  'rule.reject': 'Declined',
  'rule.amend': 'Amended',
  'rule.restate': 'Restated what it covers',
  'clause.cite': 'Cited to a document',
  'clause.disagree': 'Disagreement raised',
  'clause.resolve': 'Disagreement answered',
  'rule.review.confirm': 'Confirmed unchanged at review',
}

export function RuleHistory({ ruleId }: { ruleId: string }) {
  const [history, setHistory] = useState<History | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    api
      .history(ruleId)
      .then((h) => live && setHistory(h))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      live = false
    }
  }, [ruleId])

  if (error)
    return (
      <p className="error" role="alert">
        {error}
      </p>
    )
  if (!history) return <p className="loading">Loading history…</p>

  return (
    <div className="history" data-testid="history">
      <h4>
        History of <code>{history.rule_key}</code>
      </h4>

      <h5>What it said, and when</h5>
      <table className="grid versions">
        <thead>
          <tr>
            <th>Standing</th>
            <th>Said</th>
            <th>Covers</th>
            <th>In force from</th>
            <th>Until</th>
            <th>Cited</th>
          </tr>
        </thead>
        <tbody>
          {history.versions.map((v) => {
            const said = says(v)
            return (
              <tr key={v.rule_id} data-testid={`version-${v.status}`}>
                <td>
                  <span className={`standing standing-${v.status}`}>{v.status}</span>
                </td>
                <td>
                  <span className={said.numeric ? 'limit' : 'no-number'}>{said.text}</span>
                </td>
                {/* A restatement changes only this, so without it two versions
                    read as the same limit twice. */}
                <td>{subjectText(v.subject)}</td>
                <td>{when(v.effective_from)}</td>
                <td>
                  {v.effective_to ? (
                    when(v.effective_to)
                  ) : (
                    <b>still in force</b>
                  )}
                </td>
                <td className="clause">{v.source_clause ?? '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <h5>What was decided, and by whom</h5>
      <table className="grid events">
        <thead>
          <tr>
            <th>What happened</th>
            <th>When</th>
            <th>Who</th>
            <th>Reason given</th>
          </tr>
        </thead>
        <tbody>
          {history.events.map((e) => (
            <tr key={e.audit_event_id} data-testid="event">
              <td>
                {ACTIONS[e.action] ?? e.action}
                <span className="clause">{e.action}</span>
              </td>
              <td>{moment(e.occurred_at)}</td>
              {/* A decision with nobody's name on it is a decision nobody made.
                  `system` is a real actor here — drift clearing itself (§9.2). */}
              <td className="clause">{e.actor_user_id ?? e.actor_type}</td>
              <td>
                {typeof e.after?.reason === 'string' ? e.after.reason : <span className="clause">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
